<template>
    <div class="list-container" :class="{ 'mobile': getMobileAppState }">
        <!-- Tabs -->
        <tabs-generic class="tabs-container" :tabsList="tabsList" @clicked="tabClickHandler"/>
        <!-- Payments Operators List -->
        <div class="list-card">
            <div
            v-for="(paymentOperator, index) in parsedOperators"
            class="card"
            :key="`operator${paymentOperator.name}`"
            @click="handleOperatorClick(paymentOperator, index)">
                <!-- Image -->
                <img :key="getAppThemeMode" class="row-image" :src="imageHandler(paymentOperator.imageUrl)" @error="errorImage"/>
                <!-- Text: Entity or Type -->
                <div class="row-text">{{paymentOperator.name}}</div>
                <!-- More Options -->
                <dropdown-more-options
                v-if="paymentOperator.children.length > 1"
                icon="arrow-down"
                :triggerOpen="paymentOperator.triggerOpen"
                :options="paymentOperator.children"
                @removeTriggerOpen="operatorWithChildrenClickedIndex = null"
                @clicked="childrenOperatorClickHandler($event, paymentOperator)"/>
            </div>
        </div>
    </div>
</template>

<script setup>
import TabsGeneric from '@/components/generic/tabs/Generic'
import DropdownMoreOptions from '@/components/generic/dropdowns/moreOptions/MoreOptions'
/* Vue */
import { ref, computed } from 'vue'

/* Assets & Helpers */
import { categoryIconMapper, paymentsFlowsRoute } from '@/assets/scripts/mappers/payments'
import { navigateByName } from '@/router/helpers/navigation'

/* Store */
import { mapGetters } from '@/store/helpers/mappers'

/* mapGetters */
const { getMobileAppState, getAppThemeMode } = mapGetters()

/** props received
@paymentsOperatorsList { Array } - Payments Operators List
*/
const props = defineProps({
    paymentsOperatorsList: Array
})

/**********************************************************************
*                                                                     *
*                              Tabs Logic                             *
*                                                                     *
**********************************************************************/
const selectedTab = ref(props.paymentsOperatorsList[0].name)

const tabsList = computed(() => {
    return props.paymentsOperatorsList.map(tab => ({
        id: tab.name,
        label: tab.name,
        illustration: categoryIconMapper(tab.categoryId),
        selected: selectedTab.value === tab.name
    }))
})

const tabClickHandler = $event => {
    selectedTab.value = $event

    /* To force when clicking on "pagamentos ao estado" to open flow instead of cards options */
    if (parsedOperators.value.length === 1) {
        const operatorData = parsedOperators.value[0].children[0]

        if (operatorData.data && operatorData.data.emisReference === '600000') {
            return navigateToPayment(operatorData)
        }
    }
}

/**********************************************************************
*                                                                     *
*                              Operators                              *
*                                                                     *
**********************************************************************/
const operatorWithChildrenClickedIndex = ref(null)
const parsedOperators = computed(() => {
    return props.paymentsOperatorsList.find(tab => selectedTab.value === tab.name).children.map((children, index) => {
        return {
            ...children,
            triggerOpen: operatorWithChildrenClickedIndex.value === index,
            children: !children.children ? [] : children.children.map(lastChildren => {
                return { ...lastChildren, text: lastChildren.name }
            })
        }
    })
})

const imageHandler = img => {
    return img || `./images${categoryIconMapper(selectedTab.value, getAppThemeMode.value === 'dark')}.svg`
}

const errorImage = event => {
    event.target.src = `./images${categoryIconMapper(selectedTab.value, getAppThemeMode.value === 'dark')}.svg`
}

const handleOperatorClick = ($event, index) => {
    if ($event.children.length === 1) navigateToPayment($event.children[0])
    else operatorWithChildrenClickedIndex.value = index
}

const childrenOperatorClickHandler = ($event, paymentOperator) => {
    navigateToPayment(paymentOperator.children.find(obj => obj.id === $event))
}

const navigateToPayment = entry => {
    navigateByName(paymentsFlowsRoute(entry.flow), { params: entry })
}
</script>

<style lang="scss" scoped>
.list-container {
    width: 100%;

    .generic-tabs-wrapper {
        margin-bottom: 24px;
    }

    .list-card {
        @include cardGeneral1;
        width: 100%;
        padding: 16px;

        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 16px;

        .card {
            width: 100%;
            padding: 16px;

            display: flex;
            align-items: center;
            gap: 8px;

            border-radius: 8px;
            border: 1px solid var(--secondary-light-1);
            cursor: pointer;

            .row-image {
                width: 32px;
                height: 32px;

                display: flex;
                flex-shrink: 0;

                border-radius: 50%;
                border: 1px solid var(--secondary-light-2);
                background-color: var(--secondary-light-2);
                object-fit: scale-down;
            }

            .row-text {
                width: 100%;

                @include H7-Medium;
                color: var(--secondary-dark-2);
                text-overflow: ellipsis;
                white-space: nowrap;
                overflow: hidden;
            }

            .dropdown-wrapper {
                flex-shrink: 0;
            }
        }
    }

    &.mobile {
        .list-card {
            grid-template-columns: 1fr;
            overflow: hidden;

            .card {
                overflow: hidden;
            }
        }
    }
}
</style>
